import { SagaIterator } from '@redux-saga/core';
import { all, takeLatest, put, call, select } from 'redux-saga/effects';

import { isEmpty } from 'lodash';
import { statusMessageSaga } from '../AppManagement/saga';
import { RequestSagaParams } from '../../types';
import { getDecodedQueryParams } from '../../utils';

import * as Actions from './slice';
import * as Endpoints from './endpoints';
import { mapChannelStream, extractEmbedded } from './mappings';
import { IChannel, IPlacement, IPlaylist, IStream } from './types';

export function* getVideoList({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getVideoList, payload);
    yield put(Actions.fetchVideoListSuccess(response.data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchVideoListFailure(error));
  }
}

// export function* updateUpload({ payload }: RequestSagaParams): SagaIterator {
//   const params = getDecodedQueryParams() as { page: string };
//   try {
//     const { newTags, info, conversionToCTA } = mapUpdateUpload(payload.data);
//     yield call(Endpoints.putUploadContent, payload.id, info);
//     if (!payload.isTagsEqual) {
//       yield call(Endpoints.putUploadTags, payload.id, newTags);
//     }
//     if (!payload.isCTAEqual) {
//       const ctaResponse = yield call(Endpoints.postConversionToCTA, conversionToCTA);
//       if (ctaResponse && ctaResponse.data.id) {
//         yield call(Endpoints.putUpdateCTA, payload.id, ctaResponse?.data?.id);
//       }
//     }
//     yield put(Actions.fetchVideoList({ marketerId: payload.marketerId, page: Number(params.page) - 1 || 0 }));
//     if (payload.callback) {
//       yield call(payload.callback);
//     }
//     yield put(Actions.updateUploadSuccess());
//     yield call(statusMessageSaga, { type: 'success', message: 'Successfully uploaded the content' });
//   } catch (error: any) {
//     yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
//     yield put(Actions.updateUploadFailure(error));
//   }
// }

interface IYieldAllErrorPromise<T> {
  success: boolean;
  data?: T;
  error?: any;
}

// Generic wrapper for safe API calls
function* yieldAllApiHandler<T>(
  apiFn: (...args: any[]) => Promise<T>,
  ...args: any[]
): Generator<any, IYieldAllErrorPromise<T>, T> {
  try {
    const response: T = yield call(apiFn, ...args);
    return { success: true, data: response };
  } catch (error) {
    return { success: false, error };
  }
}

export function* getChannelList({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getChannelsByMarketer, payload);
    let streamsResponse, placementResponse, playlistResponse;

    const channelMapper: { [key: string]: IChannel } = {};
    const streamMapper: { [key: string]: IStream } = {};
    const placementMapper: { [key: string]: IPlacement } = {};
    const playlistMapper: { [key: string]: IPlaylist } = {};
    const placementPlaylistMapper: { [key: string]: string } = {};

    if (response?.data?._embedded?.channels && !isEmpty(response?.data?._embedded?.channels)) {
      response.data._embedded.channels.forEach((channel: IChannel) => {
        channelMapper[channel.id] = channel;
      });

      streamsResponse = yield all(
        response?.data?._embedded?.channels.map((item: { id: string }) => {
          return call(Endpoints.getStreamsByChannel, item.id);
        })
      );
    }
    const streams: any = [];
    if (!isEmpty(streamsResponse)) {
      streamsResponse.forEach((stream: any) => {
        if (stream.data?._embedded?.streams) {
          stream.data._embedded.streams.forEach((streamData: any) => streams.push(streamData));
        }
      });
    }

    // const streams = streamsResponse.map((stream: any) => stream.data?._embedded?.streams || []).flat();

    if (!isEmpty(streams)) {
      streams.forEach((stream: IStream) => {
        streamMapper[stream.id] = stream;
      });

      placementResponse = yield all(
        streams.map((stream: { id: string }) => {
          return call(Endpoints.getPlacementsByStream, stream.id);
        })
      );
    }

    const placementList: any = [];
    if (!isEmpty(placementResponse)) {
      placementResponse.forEach((placement: any) => {
        if (placement.data?._embedded?.placements) {
          placement.data._embedded.placements.forEach((place: any) => placementList.push(place));
        }
      });
    }

    if (!isEmpty(placementList)) {
      placementList.forEach((placement: IPlacement) => {
        placementMapper[placement.id] = placement;
      });

      playlistResponse = yield all(
        placementList.map((placement: { id: string }) => {
          // return call(Endpoints.getPlaylistByPlacementId, placement.id);
          return yieldAllApiHandler(Endpoints.getPlaylistByPlacementId, placement.id);
        })
      );
    }

    const playlistList = !isEmpty(playlistResponse)
      ? playlistResponse.map((playList: any) => (playList?.data ? playList?.data.data : [])).flat()
      : [];

    if (!isEmpty(playlistList)) {
      playlistList.forEach((playList: IPlaylist) => {
        playlistMapper[playList.id] = playList;

        playList?.placements &&
          playList?.placements.length > 0 &&
          playList?.placements?.forEach((placementId: string) => {
            placementPlaylistMapper[placementId] = playList.id;
          });
      });
    }
    yield put(
      Actions.getChannelsByMarketerIdSuccess({
        channels: channelMapper,
        stream: streamMapper,
        placementList: placementMapper,
        playlistList: playlistMapper,
        placementPlaylistMapper: placementPlaylistMapper
      })
      // Actions.getChannelsByMarketerIdSuccess(
      //   mapChannelStream({
      //     channelList: response.data,
      //     streamList: streamsResponse.map((item: { data: any[] }) => item.data),
      //     placementList: placementResponse.map((item: { data: any[] }) => item.data)
      //   })
      // )
    );
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getChannelsByMarketerIdFailure(error));
  }
}

export function* updatePlacementTags({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.putUpdatePlacementTags, payload.id, payload.tags);
    // yield put(Actions.getChannelsByMarketerId(payload.marketerId));
    if (payload.callback) {
      yield call(payload.callback);
    }
    yield put(Actions.updatePlacementTagsSuccess(response.data));
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully updated tags' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updatePlacementTagsFailure(error));
  }
}

export function* getTagsByMarketer(): SagaIterator {
  try {
    const response = yield call(Endpoints.getTagsByMarketer);
    yield put(Actions.getTagsByMarketerSuccess(response.data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getChannelsByMarketerIdFailure(error));
  }
}

export function* disableVideo({ payload }: RequestSagaParams): SagaIterator {
  const params = getDecodedQueryParams() as { page: string };
  try {
    yield call(Endpoints.deleteVideo, payload.id);
    // yield put(Actions.getChannelsByMarketerId(payload.marketerId));
    yield put(Actions.fetchVideoList({ marketerId: payload.marketerId, page: Number(params.page) - 1 || 0 }));
    if (payload.callback) {
      yield call(payload.callback);
    }
    yield put(Actions.disableVideoSuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Content Disabled.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.disableVideoFailure(error));
  }
}

export function* fetchPlaylistByFeedId({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getPlaylistByPlacementId, payload.feedId);
    yield put(Actions.fetchPlaylistByFeedIdSuccess(response.data));
    if (payload.callback) {
      yield call(payload.callback, response.data);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchPlaylistByFeedIdFailure(error));
  }
}

export function* createPlaylist({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.createPlaylist, payload);
    yield put(Actions.createPlaylistSuccess(response.data));
    if (payload.callback) {
      yield call(payload.callback, response.data);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Playlist created successfully.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.createPlaylistFailure(error));
  }
}

export function* linkFeedsToPlaylist({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.linkPlacementsToPlaylist, payload.playlistId, {
      placements: payload.placements
    });
    yield put(Actions.linkFeedsToPlaylistSuccess(response.data));
    if (payload.callback) {
      yield call(payload.callback);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Feeds linked to playlist successfully.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.linkFeedsToPlaylistFailure(error));
  }
}

export function* fetchContentsByPlaylist({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getContentsByPlaylist, payload);
    yield put(Actions.fetchContentsByPlaylistSuccess(response.data));
    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchContentsByPlaylistFailure(error));
  }
}

export function* linkPlaylistsToContent({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.linkPlaylistsToContent, payload.contentId, { playlists: payload.playlists });
    const state = yield select();
    if (
      state.assetsManagement.selectedPlacementId &&
      state.assetsManagement.placementPlaylistMapper[state.assetsManagement.selectedPlacementId] ===
        payload.playlists[0]
    ) {
      yield put(Actions.fetchVideoPlayListForPlacement({ playlistId: payload.playlists[0] }));
    }
    if (payload.callback) {
      yield call(payload.callback);
    }
    yield put(Actions.linkPlaylistsToContentSuccess(response.data));
    yield call(statusMessageSaga, { type: 'success', message: 'Playlists linked to content successfully.' });
  } catch (error: any) {
    yield put(Actions.linkPlaylistsToContentFailure(error));
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail || error?.error });
  }
}

export function* linkPlaylistsToListOfContents({ payload }: RequestSagaParams): SagaIterator {
  try {
    const linkContentToPlaylistResponse = yield all(
      payload.contents.map((content: any) => {
        return call(Endpoints.linkPlaylistsToContent, content.id, { playlists: payload.playlists });
      })
    );
    if (payload.callback) {
      yield call(payload.callback);
    }

    const updatedAssets = linkContentToPlaylistResponse.map((res: any) => res.data);
    yield put(Actions.linkPlaylistsToListOfContentsSuccess(updatedAssets));
    yield call(statusMessageSaga, { type: 'success', message: 'Playlists linked to content successfully.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.linkPlaylistsToListOfContentsFailure(error));
  }
}

export function* findOrCreatePlaylistByFeedId({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { feedId, marketerId, callback } = payload;
    let playlist = null;

    try {
      const playlistResponse = yield call(Endpoints.getPlaylistByPlacementId, feedId);
      playlist = playlistResponse.data;
    } catch (fetchError: any) {
      if (fetchError.response?.status !== 404 && fetchError.status !== 404) {
        throw fetchError;
      }
    }

    if (!playlist) {
      const playlistName = `Playlist for ${feedId}`;
      const createResponse = yield call(Endpoints.createPlaylist, { name: playlistName, marketer: marketerId });

      if (createResponse.data) {
        yield call(Endpoints.linkPlacementsToPlaylist, createResponse.data.id, {
          placements: [feedId]
        });

        const updatedResponse = yield call(Endpoints.getPlaylistByPlacementId, feedId);
        playlist = updatedResponse.data || createResponse.data;
      }
    }

    yield put(Actions.findOrCreatePlaylistByFeedIdSuccess(playlist));

    if (callback) {
      yield call(callback, playlist);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.findOrCreatePlaylistByFeedIdFailure(error));
  }
}

export function* fetchAllVideos({ payload }: RequestSagaParams): SagaIterator {
  try {
    interface IContentItem {
      id: string;
      title?: string;
      [key: string]: any;
    }

    const videosResponse = yield call(Endpoints.fetchAllVideos, payload);
    const contentList = videosResponse.data._embedded?.contents || [];
    const validContentItems = contentList.filter((content: IContentItem) => content.id);
    const enrichedVideos = [];
    for (const videoContent of validContentItems) {
      enrichedVideos.push({
        ...videoContent,
        thumbnail: videoContent.asset?.thumbnail
      });
    }

    yield put(Actions.fetchAllVideosSuccess(enrichedVideos));

    if (payload.callback) {
      yield call(payload.callback, enrichedVideos);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchAllVideosFailure(error));
  }
}

export function* fetchVideoPlayListForPlacement({ payload }: RequestSagaParams): SagaIterator {
  const { feedId, marketerId, playlistId, pageNo } = payload;

  let unpinnedContent = [];
  let page = {};
  try {
    const unpinnedResponse = yield call(Endpoints.fetchUnpinnedPlaylistContent, playlistId, pageNo);
    unpinnedContent = unpinnedResponse?.data?._embedded?.contents || [];
    page = unpinnedResponse?.data?.page;
  } catch (error) {
    yield put(Actions.fetchVideoPlayListForPlacementFailure(error));
    console.error('fetchUnpinnedPlaylistContent error:', error);
  }

  yield put(Actions.fetchVideoPlayListForPlacementSuccess({ content: unpinnedContent, page: page }));
  if (payload.callback) {
    yield call(payload.callback, unpinnedContent, page);
  }
}

export function* fetchPinnedVideoPlayListForPlacement({ payload }: RequestSagaParams): SagaIterator {
  const { feedId, marketerId, playlistId, pageNo } = payload;

  try {
    const pinnedResponse = yield call(Endpoints.fetchPinnedPlaylistContent, playlistId);

    yield put(Actions.fetchPinnedVideoPlayListForPlacementSuccess(pinnedResponse?.data?._embedded?.contents || []));
  } catch (error) {
    yield put(Actions.fetchPinnedVideoPlayListForPlacementFailure(error));
    console.error('fetchUnpinnedPlaylistContent error:', error);
  }

  // if (payload.callback) {
  //   yield call(payload.callback, unpinnedContent, page);
  // }
}

export function* searchVideosInPlaylist({ payload }: RequestSagaParams): SagaIterator {
  const { keyword, playlistId } = payload;

  let searchVideos = [];
  try {
    const searchVideosResponse = yield call(Endpoints.searchVideos, playlistId, keyword);
    searchVideos = searchVideosResponse?.data?._embedded?.contents || [];
  } catch (error) {
    yield put(Actions.searchVideosInPlaylistFailure(error));
    console.error('fetchUnpinnedPlaylistContent error:', error);
  }

  yield put(Actions.searchVideosInPlaylistSuccess(searchVideos));
  if (payload.callback) {
    yield call(payload.callback, searchVideos);
  }
}

export function* fetchContentForFeed({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { feedId, marketerId } = payload;
    let playlistId: string | null = null;
    const findPlaylistAction = {
      type: Actions.findOrCreatePlaylistByFeedId.type,
      payload: {
        feedId,
        marketerId,
        callback: function* (playlist: any) {
          playlistId = playlist?.id || null;
          yield null;
        }
      }
    };

    yield call(findOrCreatePlaylistByFeedId, findPlaylistAction);
    if (!playlistId) {
      throw new Error('Playlist not found or created');
    }

    let pinnedContent = [];
    let unpinnedContent = [];
    let hasPinnedError = false;
    let hasUnpinnedError = false;

    let pinnedCount = 0;
    let unPinnedCount = 0;
    try {
      const pinnedResponse = yield call(Endpoints.fetchPinnedPlaylistContent, playlistId);
      pinnedContent = pinnedResponse?.data?._embedded?.contents || [];
      pinnedCount = 0; //pinnedContent.length;
    } catch (error) {
      hasPinnedError = true;
      console.error('fetchPinnedPlaylistContent error:', error);
    }
    let page = {};
    try {
      const unpinnedResponse = yield call(Endpoints.fetchUnpinnedPlaylistContent, playlistId, payload.pageNo);
      unpinnedContent = unpinnedResponse?.data?._embedded?.contents || [];
      unPinnedCount = unpinnedResponse?.data?.page.totalElements;
      page = unpinnedResponse?.data?.page;
    } catch (error) {
      hasUnpinnedError = true;
      console.error('fetchUnpinnedPlaylistContent error:', error);
    }

    if (hasPinnedError && hasUnpinnedError) {
      yield put(Actions.fetchContentForFeedFailure('Failed to fetch pinned and unpinned content for the feed'));
      return;
    }

    const enrichedVideos = [];
    for (const { videoContent, pinned } of [
      // ...pinnedContent.map((v: any) => ({ videoContent: v, pinned: true })),
      ...unpinnedContent.map((v: any) => ({ videoContent: v, pinned: false }))
    ]) {
      if (videoContent.id) {
        enrichedVideos.push({
          ...videoContent,
          thumbnail: videoContent.asset?.thumbnail,
          ...(pinned ? { pinned: true } : {})
        });
      }
    }

    yield put(Actions.fetchContentForFeedSuccess(enrichedVideos));

    if (payload.callback) {
      yield call(payload.callback, unpinnedContent, page, playlistId);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchContentForFeedFailure(error));
  }
}

export function* unlinkContentFromPlaylist({ payload }: RequestSagaParams): SagaIterator {
  try {
    const assetData = yield call(Endpoints.unlinkContentFromPlaylist, payload.contentId, payload.playlistId);

    // if (payload.playlistId) {
    //   yield put(Actions.fetchContentsByPlaylist({ playlistId: payload.playlistId }));
    // }

    const state = yield select();
    if (
      state.assetsManagement.selectedPlacementId &&
      state.assetsManagement.placementPlaylistMapper[state.assetsManagement.selectedPlacementId] === payload.playlistId
    ) {
      yield put(Actions.fetchPinnedVideoPlayListForPlacement({ playlistId: payload.playlistId }));
      yield put(Actions.fetchVideoPlayListForPlacement({ playlistId: payload.playlistId }));
    }

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield put(Actions.unlinkContentFromPlaylistSuccess(assetData.data));
    yield call(statusMessageSaga, { type: 'success', message: 'Content unlinked from playlist successfully.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.unlinkContentFromPlaylistFailure(error));
  }
}

export function* fetchAssetDetails({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchAssetDetails, payload.assetId);
    yield put(
      Actions.fetchAssetDetailsSuccess({
        assetId: payload.assetId,
        data: response.data
      })
    );

    if (payload.callback) {
      yield call(payload.callback, response.data);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchAssetDetailsFailure(error));
  }
}

export function* reorderPlaylistContent({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.reorderPlaylistContent, payload.playlistId, payload.fromIndex - 1, payload.toIndex - 1);

    const state = yield select();

    if (
      state.assetsManagement.selectedPlacementId &&
      state.assetsManagement.placementPlaylistMapper[state.assetsManagement.selectedPlacementId] === payload.playlistId
    ) {
      yield put(Actions.fetchPinnedVideoPlayListForPlacement({ playlistId: payload.playlistId }));
    }

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield put(Actions.reorderPlaylistContentSuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Playlist content reordered successfully.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.reorderPlaylistContentFailure(error));
  }
}

export function* pinContentToPlaylist({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.linkPlaylistsToContent, payload.contentId, { playlists: [payload.playlistId] });

    yield call(Endpoints.pinContentToPlaylist, payload.playlistId, {
      contentId: payload.contentId,
      forced: payload.forced || false
    });

    // if (payload.playlistId) {
    //   yield put(Actions.fetchContentsByPlaylist({ playlistId: payload.playlistId }));
    // }

    const state = yield select();
    if (
      state.assetsManagement.selectedPlacementId &&
      state.assetsManagement.placementPlaylistMapper[state.assetsManagement.selectedPlacementId] === payload.playlistId
    ) {
      yield put(Actions.fetchPinnedVideoPlayListForPlacement({ playlistId: payload.playlistId }));
      yield put(Actions.fetchVideoPlayListForPlacement({ playlistId: payload.playlistId }));
    }

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield put(Actions.pinContentToPlaylistSuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Content pinned to playlist successfully.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.pinContentToPlaylistFailure(error));
  }
}

export function* unpinContentFromPlaylist({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.unpinContentFromPlaylist, payload.playlistId, payload.contentId);

    // if (payload.playlistId) {
    //   yield put(Actions.fetchContentsByPlaylist({ playlistId: payload.playlistId }));
    // }

    const state = yield select();
    if (
      state.assetsManagement.selectedPlacementId &&
      state.assetsManagement.placementPlaylistMapper[state.assetsManagement.selectedPlacementId] === payload.playlistId
    ) {
      yield put(Actions.fetchPinnedVideoPlayListForPlacement({ playlistId: payload.playlistId }));
      yield put(Actions.fetchVideoPlayListForPlacement({ playlistId: payload.playlistId }));
    }

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield put(Actions.unpinContentFromPlaylistSuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Content unpinned from playlist successfully.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.unpinContentFromPlaylistFailure(error));
  }
}

export function* fetchPinnedPlaylistContent({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchPinnedPlaylistContent, payload.playlistId);

    yield put(Actions.fetchPinnedPlaylistContentSuccess(response.data));

    if (payload.callback) {
      yield call(payload.callback, response.data);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchPinnedPlaylistContentFailure(error));
  }
}

export function* fetchUnpinnedPlaylistContent({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchUnpinnedPlaylistContent, payload.playlistId);

    yield put(Actions.fetchUnpinnedPlaylistContentSuccess(response.data));

    if (payload.callback) {
      yield call(payload.callback, response.data);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchUnpinnedPlaylistContentFailure(error));
  }
}

export function* removePinnedContentFromPlaylist({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.removePinnedContentFromPlaylist, payload.playlistId, payload.contentId);
    const state = yield select();
    if (
      state.assetsManagement.selectedPlacementId &&
      state.assetsManagement.placementPlaylistMapper[state.assetsManagement.selectedPlacementId] === payload.playlistId
    ) {
      yield put(Actions.fetchPinnedVideoPlayListForPlacement({ playlistId: payload.playlistId }));
      yield put(Actions.fetchVideoPlayListForPlacement({ playlistId: payload.playlistId }));
    }
    yield put(Actions.removePinnedContentFromPlaylistSuccess(response.data));

    yield call(statusMessageSaga, { type: 'success', message: 'Content unlinked from playlist successfully.' });
    if (payload.callback) {
      yield call(payload.callback, response.data);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.removePinnedContentFromPlaylistFailure(error));
  }

  // yield put(Actions.unlinkContentFromPlaylistSuccess(assetData.data));
  //   yield call(statusMessageSaga, { type: 'success', message: 'Content unlinked from playlist successfully.' });
  // } catch (error: any) {
  //   yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
  //   yield put(Actions.unlinkContentFromPlaylistFailure(error));
}

export function* AssetsManagementWatcherSaga(): SagaIterator {
  yield all([
    yield takeLatest(Actions.fetchVideoList.type, getVideoList),
    // yield takeLatest(Actions.updateUpload.type, updateUpload),
    yield takeLatest(Actions.getChannelsByMarketerId.type, getChannelList),
    yield takeLatest(Actions.updatePlacementTags.type, updatePlacementTags),
    yield takeLatest(Actions.getTagsByMarketer.type, getTagsByMarketer),
    yield takeLatest(Actions.disableVideo.type, disableVideo),
    yield takeLatest(Actions.createPlaylist.type, createPlaylist),
    yield takeLatest(Actions.linkFeedsToPlaylist.type, linkFeedsToPlaylist),
    yield takeLatest(Actions.fetchContentsByPlaylist.type, fetchContentsByPlaylist),
    yield takeLatest(Actions.linkPlaylistsToContent.type, linkPlaylistsToContent),
    yield takeLatest(Actions.linkPlaylistsToListOfContents.type, linkPlaylistsToListOfContents),
    yield takeLatest(Actions.fetchPlaylistByFeedId.type, fetchPlaylistByFeedId),
    yield takeLatest(Actions.fetchPinnedVideoPlayListForPlacement.type, fetchPinnedVideoPlayListForPlacement),
    yield takeLatest(Actions.findOrCreatePlaylistByFeedId.type, findOrCreatePlaylistByFeedId),
    yield takeLatest(Actions.fetchContentForFeed.type, fetchContentForFeed),
    yield takeLatest(Actions.fetchVideoPlayListForPlacement.type, fetchVideoPlayListForPlacement),
    yield takeLatest(Actions.searchVideosInPlaylist.type, searchVideosInPlaylist),
    yield takeLatest(Actions.unlinkContentFromPlaylist.type, unlinkContentFromPlaylist),
    yield takeLatest(Actions.fetchAssetDetails.type, fetchAssetDetails),
    yield takeLatest(Actions.fetchAllVideos.type, fetchAllVideos),
    yield takeLatest(Actions.reorderPlaylistContent.type, reorderPlaylistContent),
    yield takeLatest(Actions.pinContentToPlaylist.type, pinContentToPlaylist),
    yield takeLatest(Actions.unpinContentFromPlaylist.type, unpinContentFromPlaylist),
    yield takeLatest(Actions.fetchPinnedPlaylistContent.type, fetchPinnedPlaylistContent),
    yield takeLatest(Actions.fetchUnpinnedPlaylistContent.type, fetchUnpinnedPlaylistContent),
    yield takeLatest(Actions.removePinnedContentFromPlaylist.type, removePinnedContentFromPlaylist)
  ]);
}

export default AssetsManagementWatcherSaga;
